import { formatRecordId } from "../lib/utils-format";
import { DuplicateApnDialog, duplicateFromError } from "../components/DuplicateApnDialog";
import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Search, Mail, MapPin, Loader2, Save, Sparkles, Database, ShieldCheck, AlertTriangle,
  HelpCircle, Layers, X, ChevronDown, Building2, Ruler, UserRound,
  Landmark, Plug, CheckCircle2, SlidersHorizontal,
  Vote, Scale, PanelRightClose, PanelRightOpen,
} from "lucide-react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const CATEGORY_META = [
  { key: "identity", label: "Property Details", icon: MapPin },
  { key: "parcel", label: "Parcel Information", icon: Ruler },
  { key: "ownership", label: "Ownership", icon: UserRound },
  { key: "zoning", label: "Zoning", icon: Landmark },
];

const STATUS_STYLES = {
  Confirmed: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  Preliminary: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  "Needs Verification": "bg-red-500/15 text-red-500 border-red-500/30",
  Unknown: "bg-muted text-muted-foreground border-border",
};
const STATUS_ICON = {
  Confirmed: CheckCircle2,
  Preliminary: HelpCircle,
  "Needs Verification": AlertTriangle,
  Unknown: HelpCircle,
};

const DISCOVERY_FILTERS = [
  "Vacant land", "Infill lots", "Tear-down properties", "Large residential lots",
  "Subdivision candidates", "Underutilized parcels", "Min lot-size met", "Specific zoning district",
  "Acreage range", "Lot-size range", "Has sewer", "Has public water", "No HOA",
];

// USA-map market-context overlays (state level).
const POL_COLORS = { red: "#dc2626", blue: "#2563eb", purple: "#9333ea" };
const REG_COLORS = { low: "#22c55e", moderate: "#eab308", high: "#ef4444" };
const POL_LEGEND = [["red", "Red State", "#dc2626"], ["blue", "Blue State", "#2563eb"], ["purple", "Purple / Swing State", "#9333ea"]];
const REG_LEGEND = [["low", "Low Restriction", "#22c55e"], ["moderate", "Moderate Restriction", "#eab308"], ["high", "High Restriction", "#ef4444"]];


const ZONE_COLORS = { residential: "#3b82f6", commercial: "#ef4444", industrial: "#a855f7", agricultural: "#22c55e", mixed: "#eab308", other: "#f5b301" };
const ZONE_LEGEND = [["residential", "Residential", "#3b82f6"], ["commercial", "Commercial", "#ef4444"], ["industrial", "Industrial", "#a855f7"], ["agricultural", "Agricultural", "#22c55e"], ["mixed", "Mixed / PUD", "#eab308"], ["other", "Other", "#94a3b8"]];
function zoningCategory(record) {
  const z = (((record?.categories?.zoning?.zoning) || {}).value || "").toString().toLowerCase();
  if (!z) return "other";
  if (/(^|[^a-z])(r-?\d|res|resid|dwelling|single|multi|residence)/.test(z)) return "residential";
  if (/(^|[^a-z])(c-?\d|comm|retail|business|office)/.test(z)) return "commercial";
  if (/(^|[^a-z])(m-?\d|i-?\d|ind|manufactur|industr|warehouse)/.test(z)) return "industrial";
  if (/(^|[^a-z])(a-?\d|ag|agri|rural|farm|open space)/.test(z)) return "agricultural";
  if (/mixed|mu\b|pud|planned/.test(z)) return "mixed";
  return "other";
}

function StatusChip({ status }) {
  const Icon = STATUS_ICON[status] || HelpCircle;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[status] || STATUS_STYLES.Unknown}`}
      data-testid={`pi-status-${status.replace(/\s+/g, "-").toLowerCase()}`}>
      <Icon className="h-2.5 w-2.5" />{status}
    </span>
  );
}

function FieldRow({ field }) {
  const has = field.value !== null && field.value !== undefined && field.value !== "";
  return (
    <div className="py-2 border-b border-border/60 last:border-0" data-testid={`pi-field-${field.label.replace(/\W+/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs text-muted-foreground shrink-0 w-32">{field.label}</span>
        <span className={`text-sm text-right flex-1 ${has ? "text-foreground" : "text-muted-foreground/50 italic"}`}>
          {has ? String(field.value) : "—"}
        </span>
      </div>
      <div className="flex items-center justify-end gap-1.5 mt-1 flex-wrap">
        {field.conflict && (
          <span className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/15 text-red-500 px-2 py-0.5 text-[10px] font-medium">
            <AlertTriangle className="h-2.5 w-2.5" />Conflict
          </span>
        )}
        <StatusChip status={field.verification_status} />
        {field.source && <span className="text-[10px] text-muted-foreground/70">· {field.source}</span>}
        {field.confidence_level && <span className="text-[10px] text-muted-foreground/70">· {field.confidence_level}</span>}
      </div>
      {field.conflict && field.candidates?.length > 1 && (
        <div className="mt-1.5 rounded-md bg-red-500/5 border border-red-500/20 p-2 space-y-1">
          {field.candidates.map((c, i) => (
            <div key={i} className="flex items-center justify-between text-[11px]">
              <span className="text-foreground">{String(c.value)}</span>
              <span className="text-muted-foreground/70">{c.source}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PropertyIntelligence() {
  const location = useLocation();
  const navigate = useNavigate();
  const leadRef = useRef(null);
  const [leadBanner, setLeadBanner] = useState(null);
  const [leadUpdateMode, setLeadUpdateMode] = useState(false);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const statesGeoRef = useRef(null);
  const [query, setQuery] = useState("");
  const [queryType, setQueryType] = useState("apn");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [record, setRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [zoningLoading, setZoningLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingLeads, setSavingLeads] = useState(false);
  const [dupInfo, setDupInfo] = useState(null);
  const [postSave, setPostSave] = useState(null); // { resource, recordId } after a successful save
  // New-lead save options chosen in the Save to Leads dialog.
  const [saveMarket, setSaveMarket] = useState("On-Market");
  const [savePropertyType, setSavePropertyType] = useState("");
  // Reset the dialog choices whenever a different parcel/record is loaded so a
  // prior property's Market/Type never carries onto a new lead.
  useEffect(() => { setSaveMarket("On-Market"); setSavePropertyType(""); }, [record?.id]);
  const [showPins, setShowPins] = useState(true);
  const showPinsRef = useRef(true);
  const [pinCount, setPinCount] = useState(0);
  const pinsRef = useRef({ type: "FeatureCollection", features: [] });
  const loadPins = async () => {
    try {
      const { data } = await api.get("/pi/saved-pins");
      pinsRef.current = { type: "FeatureCollection", features: (data.pins || []).map((p) => ({
        type: "Feature", geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: { ...p, label: `${p.stage}${p.record_no ? ` · ${formatRecordId(p.resource, p.record_no)}` : ""}` } })) };
      setPinCount(pinsRef.current.features.length);
      mapRef.current?.getSource("saved-pins")?.setData(pinsRef.current);
    } catch { /* pins are a convenience layer */ }
  };
  const savedLeadHere = record?.arqitek?.saved && record?.arqitek?.resource === "inbound_leads";
  const [skipTracing, setSkipTracing] = useState(false);
  const [providers, setProviders] = useState([]);
  const [showProviders, setShowProviders] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [openCats, setOpenCats] = useState({});
  const [satellite, setSatellite] = useState(false);
  const [zoningShade, setZoningShade] = useState(false);
  const [zoneFilter, setZoneFilter] = useState(() => new Set(["residential"]));
  const [marketMap, setMarketMap] = useState([]);
  const [polOverlay, setPolOverlay] = useState(false);
  const [regOverlay, setRegOverlay] = useState(false);
  const [polFilter, setPolFilter] = useState(() => new Set(["red", "blue", "purple"]));
  const [regFilter, setRegFilter] = useState(() => new Set(["low", "moderate", "high"]));
  const [mapZoom, setMapZoom] = useState(3.5);
  const [parcelCoverage, setParcelCoverage] = useState(null);

  const makeToggle = (setter) => (cls) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(cls)) next.delete(cls); else next.add(cls);
    return next;
  });
  const togglePol = useCallback(makeToggle(setPolFilter), []);
  const toggleReg = useCallback(makeToggle(setRegFilter), []);

  const toggleCat = useCallback((cat) => setZoneFilter((prev) => {
    const next = new Set(prev);
    if (next.has(cat)) next.delete(cat); else next.add(cat);
    return next;
  }), []);

  const runLookup = useCallback(async ({ lat, lng, q, qt, apn, parcelGeometry }) => {
    setLoadingRecord(true);
    setPanelOpen(true);
    setRecord(null);
    try {
      const { data } = await api.post("/pi/lookup", {
        lat: lat ?? null, lng: lng ?? null, query: q ?? null, query_type: qt ?? null,
        apn: apn ?? null, parcel_geometry: parcelGeometry ?? null,
      });
      setRecord(data);
      // Progressive fill: the zoning code is instant, but Angela's read of the
      // ordinance standards takes a beat — fetch it after the panel renders.
      const z = data?.categories?.zoning || {};
      if (data?.id && (!z.zoning?.value || !z.build_summary?.value)) {
        setZoningLoading(true);
        api.post(`/pi/records/${data.id}/zoning-standards`)
          .then(({ data: zd }) => {
            setRecord((prev) => (prev && prev.id === data.id)
              ? { ...prev, categories: { ...prev.categories, zoning: zd.zoning }, status_counts: zd.status_counts }
              : prev);
          })
          .catch(() => {})
          .finally(() => setZoningLoading(false));
      }
    } catch (e) {
      toast.error(apiError(e));
      setPanelOpen(false);
    } finally {
      setLoadingRecord(false);
    }
  }, []);

  // Auto-run a lookup from a lead hand-off (APN preferred, else address).
  const autoLookup = useCallback(async ({ apn, address }) => {
    if (apn) {
      try {
        const { data } = await api.get(`/pi/parcel-by-apn?apn=${encodeURIComponent(apn)}`);
        if (data?.lat != null && data?.lng != null) {
          mapRef.current?.flyTo({ center: [data.lng, data.lat], zoom: 17 });
          markerRef.current?.remove();
          if (mapRef.current) markerRef.current = new mapboxgl.Marker({ color: "#27BEAA" }).setLngLat([data.lng, data.lat]).addTo(mapRef.current);
          runLookup({ lat: data.lat, lng: data.lng, apn: data.apn, parcelGeometry: data.geometry, qt: "apn" });
          return;
        }
      } catch { /* fall through to plain APN lookup */ }
      runLookup({ apn, qt: "apn" });
      return;
    }
    if (address) {
      try {
        const url = new URL("https://api.mapbox.com/search/geocode/v6/forward");
        url.searchParams.set("q", address);
        url.searchParams.set("country", "US");
        url.searchParams.set("limit", "1");
        url.searchParams.set("access_token", TOKEN);
        const j = await (await fetch(url)).json();
        const feat = j.features?.[0];
        if (feat) {
          const [lng, lat] = feat.geometry.coordinates;
          mapRef.current?.flyTo({ center: [lng, lat], zoom: 16 });
          markerRef.current?.remove();
          if (mapRef.current) markerRef.current = new mapboxgl.Marker({ color: "#27BEAA" }).setLngLat([lng, lat]).addTo(mapRef.current);
          runLookup({ lat, lng, q: feat.properties?.full_address || address, qt: "address" });
          return;
        }
      } catch { /* fall through to plain address lookup */ }
      runLookup({ q: address, qt: "address" });
    }
  }, [runLookup]);

  // Pick up a lead hand-off (from the Leads "Convert to Property Intelligence" action).
  useEffect(() => {
    const st = location.state;
    if (!st || !st.fromLead) return;
    leadRef.current = { leadId: st.leadId, updateLeadId: st.updateLeadId || null, leadFields: st.leadFields || {}, propertyName: st.propertyName };
    setLeadUpdateMode(!!st.updateLeadId);
    setLeadBanner(st.propertyName || st.address || st.apn || "Lead");
    if (st.apn) { setQueryType("apn"); setQuery(st.apn); }
    else if (st.address) { setQueryType("address"); setQuery(st.address); }
    autoLookup({ apn: st.apn, address: st.address });
    window.history.replaceState({}, "");   // avoid re-triggering on refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!TOKEN || !containerRef.current || mapRef.current) return;
    const map = new mapboxgl.Map({
      accessToken: TOKEN,
      container: containerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      projection: "mercator",           // flat map, never the globe
      bounds: [[-125, 24], [-66.5, 49.5]],
      fitBoundsOptions: { padding: 20 },
      minZoom: 3,
      maxZoom: 19,
      maxBounds: [[-140, 16], [-58, 55]], // keep the view within the United States
      renderWorldCopies: false,
    });
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), "top-right");

    // Interactive US state boundaries — green highlight + hover fill.
    const addStateLayers = async () => {
      if (map.getSource("states")) return;
      if (!statesGeoRef.current) {
        try {
          const r = await fetch("https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json");
          statesGeoRef.current = await r.json();
        } catch { return; }
      }
      if (map.getSource("states")) return;
      try {
        map.addSource("states", { type: "geojson", data: statesGeoRef.current, generateId: true });
        // Transparent fill — only used to capture hover for border highlighting (no tint).
        map.addLayer({
          id: "states-fill", type: "fill", source: "states",
          paint: { "fill-color": "#27BEAA", "fill-opacity": 0 },
        });
        map.addLayer({
          id: "states-glow", type: "line", source: "states",
          paint: { "line-color": "#27BEAA", "line-width": 3, "line-opacity": 0.25, "line-blur": 3 },
        });
        map.addLayer({
          id: "states-line", type: "line", source: "states",
          paint: {
            "line-color": "#3fe6cf",
            "line-width": ["case", ["boolean", ["feature-state", "hover"], false], 3, 1.4],
            "line-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 1, 0.8],
          },
        });
        // Zoning choropleth (all visible parcels shaded by zoning category).
        if (!map.getSource("zoning-fc")) {
          map.addSource("zoning-fc", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
          map.addLayer({
            id: "zoning-fc-fill", type: "fill", source: "zoning-fc",
            paint: {
              "fill-color": ["match", ["get", "category"],
                "residential", "#3b82f6", "commercial", "#ef4444", "industrial", "#a855f7",
                "agricultural", "#22c55e", "mixed", "#eab308", "#94a3b8"],
              "fill-opacity": 0.35,
            },
          }, map.getLayer("lots-line") ? "lots-line" : undefined);
          map.addLayer({
            id: "zoning-fc-line", type: "line", source: "zoning-fc",
            paint: { "line-color": "#ffffff", "line-opacity": 0.25, "line-width": 0.5 },
          }, map.getLayer("lots-line") ? "lots-line" : undefined);
        }
        // Properties already saved anywhere in the pipeline.
        if (!map.getSource("saved-pins")) {
          map.addSource("saved-pins", { type: "geojson", data: pinsRef.current });
          map.addLayer({
            id: "saved-pins-circle", type: "circle", source: "saved-pins",
            layout: { visibility: showPinsRef.current ? "visible" : "none" },
            paint: {
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 3.5, 12, 6, 17, 9],
              "circle-color": ["case", ["get", "archived"], "#8a8f98", "#f97316"],
              "circle-stroke-color": "#ffffff", "circle-stroke-width": 1.5, "circle-opacity": 0.95,
            },
          });
        }
        // Free county-GIS parcel lots (fallback where Regrid has no coverage).
        if (!map.getSource("gis-parcels")) {
          map.addSource("gis-parcels", { type: "geojson", data: { type: "FeatureCollection", features: [] }, generateId: true });
          map.addLayer({
            id: "gis-parcels-fill", type: "fill", source: "gis-parcels",
            paint: { "fill-color": "#f5b301", "fill-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 0.25, 0] },
          });
          map.addLayer({
            id: "gis-parcels-line", type: "line", source: "gis-parcels",
            paint: { "line-color": "#f5b301", "line-opacity": 0.8,
              "line-width": ["interpolate", ["linear"], ["zoom"], 14, 0.4, 16, 1, 19, 2] },
          });
          map.addLayer({
            id: "gis-parcels-apn", type: "symbol", source: "gis-parcels", minzoom: 16.5,
            layout: { "text-field": ["coalesce", ["get", "apn"], ""], "text-size": 10,
              "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"], "text-allow-overlap": false },
            paint: { "text-color": "#ffe08a", "text-halo-color": "#0a0a0a", "text-halo-width": 1.2 },
          });
        }
      } catch { /* layers already present */ }
    };
    map.on("style.load", addStateLayers);
    map.on("load", () => map.resize());
    map.on("zoom", () => setMapZoom(map.getZoom()));
    map.on("idle", () => {
      if (map.getZoom() < 13) { setParcelCoverage(null); return; }
      try {
        const gf = map.querySourceFeatures("gis-parcels");
        setParcelCoverage(gf.length > 0);
      } catch { setParcelCoverage(null); }
    });

    // Free county-GIS parcels: fetch lots for the viewport when zoomed to street level.
    const refreshGis = async () => {
      if (!map.getSource("gis-parcels")) return;
      if (map.getZoom() < 15) { map.getSource("gis-parcels").setData({ type: "FeatureCollection", features: [] }); return; }
      const b = map.getBounds();
      try {
        const { data } = await api.get(`/pi/parcels-features?minx=${b.getWest()}&miny=${b.getSouth()}&maxx=${b.getEast()}&maxy=${b.getNorth()}`);
        map.getSource("gis-parcels")?.setData(data);
      } catch { /* ignore */ }
    };
    map.on("moveend", refreshGis);

    let hoveredId = null;
    map.on("mousemove", "states-fill", (e) => {
      if (!e.features?.length) return;
      if (hoveredId !== null) map.setFeatureState({ source: "states", id: hoveredId }, { hover: false });
      hoveredId = e.features[0].id;
      map.setFeatureState({ source: "states", id: hoveredId }, { hover: true });
      map.getCanvas().style.cursor = "crosshair";
    });
    map.on("mouseleave", "states-fill", () => {
      if (hoveredId !== null) map.setFeatureState({ source: "states", id: hoveredId }, { hover: false });
      hoveredId = null;
      map.getCanvas().style.cursor = "";
    });

    // Saved-pin click: what's already in the pipeline here, with a link to open it.
    map.on("click", "saved-pins-circle", (e) => {
      const feats = e.features || [];
      if (!feats.length) return;
      const esc = (t) => String(t ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      const seen = new Set();
      const rows = feats.filter((f) => !seen.has(f.properties.id) && seen.add(f.properties.id)).slice(0, 8).map(({ properties: p }) => `
          <div style="padding:4px 0;border-top:1px solid rgba(255,255,255,.08)">
            <div style="font-weight:600">${esc(p.name)}</div>
            <div style="opacity:.75">${esc(p.label)}${p.status ? " · " + esc(p.status) : ""}${String(p.archived) === "true" ? " · Archived" : ""}</div>
            ${p.address ? `<div style="opacity:.75">${esc(p.address)}</div>` : ""}
            ${p.apn ? `<div style="opacity:.75">APN ${esc(p.apn)}</div>` : ""}
            <a href="/record/${esc(p.resource)}/${esc(p.id)}" data-nav="/record/${esc(p.resource)}/${esc(p.id)}" style="color:#27BEAA;font-weight:600">Open record &rarr;</a>
          </div>`).join("");
      const popup = new mapboxgl.Popup({ closeButton: true, className: "pi-lot-popup", offset: 10, maxWidth: "260px" })
        .setLngLat(feats[0].geometry.coordinates)
        .setHTML(`<div style="font-size:12px;line-height:1.4;max-height:280px;overflow:auto" data-testid="saved-pin-popup">
          ${seen.size > 1 ? `<div style="font-weight:600;margin-bottom:2px">${seen.size} saved records here</div>` : ""}${rows}</div>`)
        .addTo(map);
      popup.getElement()?.addEventListener("click", (ev) => {
        const a = ev.target.closest?.("a[data-nav]");
        if (a) { ev.preventDefault(); popup.remove(); navigate(a.getAttribute("data-nav")); }
      });
    });
    map.on("mouseenter", "saved-pins-circle", () => { map.getCanvas().style.cursor = "pointer"; });
    map.on("mouseleave", "saved-pins-circle", () => { map.getCanvas().style.cursor = ""; });

    // County-GIS lot hover: highlight + show APN.
    const gisPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, className: "pi-lot-popup" });
    let hoveredGis = null;
    map.on("mousemove", "gis-parcels-fill", (e) => {
      if (!e.features?.length) return;
      const f = e.features[0];
      if (hoveredGis !== null) map.setFeatureState({ source: "gis-parcels", id: hoveredGis }, { hover: false });
      hoveredGis = f.id;
      map.setFeatureState({ source: "gis-parcels", id: hoveredGis }, { hover: true });
      map.getCanvas().style.cursor = "pointer";
      const apn = f.properties?.apn || "—";
      gisPopup.setLngLat(e.lngLat).setHTML(
        `<div style="font:12px system-ui;color:#fff"><b>APN:</b> ${apn}</div>`).addTo(map);
    });
    map.on("mouseleave", "gis-parcels-fill", () => {
      if (hoveredGis !== null) map.setFeatureState({ source: "gis-parcels", id: hoveredGis }, { hover: false });
      hoveredGis = null;
      map.getCanvas().style.cursor = "";
      gisPopup.remove();
    });

    const ro = new ResizeObserver(() => map.resize());
    ro.observe(containerRef.current);
    map.on("click", async (e) => {
      // A click on a saved pin only opens its popup — skip the parcel lookup.
      try {
        if (map.getLayer("saved-pins-circle") && map.queryRenderedFeatures(e.point, { layers: ["saved-pins-circle"] }).length) return;
      } catch { /* layer may be absent */ }
      const { lng, lat } = e.lngLat;
      markerRef.current?.remove();
      markerRef.current = new mapboxgl.Marker({ color: "#27BEAA" }).setLngLat([lng, lat]).addTo(map);
      // If a county-GIS lot is under the click, use its APN + boundary.
      let gisApn = null, gisGeom = null;
      try {
        const ghits = map.queryRenderedFeatures(e.point, { layers: ["gis-parcels-fill"] });
        if (ghits?.length) { gisApn = ghits[0].properties?.apn || null; gisGeom = ghits[0].geometry || null; }
      } catch { /* layer may not be present when zoomed out */ }
      // Reverse-geocode the click to an address for the other adapters.
      let addr = null;
      try {
        const u = new URL("https://api.mapbox.com/search/geocode/v6/reverse");
        u.searchParams.set("longitude", lng);
        u.searchParams.set("latitude", lat);
        u.searchParams.set("access_token", TOKEN);
        const j = await (await fetch(u)).json();
        addr = j.features?.[0]?.properties?.full_address || j.features?.[0]?.properties?.name || null;
      } catch { /* reverse geocode optional */ }
      runLookup({ lat, lng, q: addr, qt: "address", apn: gisApn, parcelGeometry: gisGeom });
    });
    return () => { ro.disconnect(); markerRef.current?.remove(); map.remove(); mapRef.current = null; };
  }, [runLookup]);

  const styleReady = useRef(false);
  useEffect(() => {
    if (!mapRef.current) return;
    if (!styleReady.current) { styleReady.current = true; return; } // skip initial mount
    mapRef.current.setStyle(satellite ? "mapbox://styles/mapbox/satellite-streets-v12" : "mapbox://styles/mapbox/dark-v11");
  }, [satellite]);

  // Keep the canvas sized correctly when the record panel opens/closes.
  useEffect(() => {
    const t = setTimeout(() => mapRef.current?.resize(), 320);
    return () => clearTimeout(t);
  }, [panelOpen]);

  // Zoning choropleth: fetch zoning polygons for the current viewport and color by category.
  const refreshZoning = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !zoningShade) return;
    if (map.getZoom() < 13) { map.getSource("zoning-fc")?.setData({ type: "FeatureCollection", features: [] }); return; }
    const b = map.getBounds();
    try {
      const { data } = await api.get(`/pi/zoning-features?minx=${b.getWest()}&miny=${b.getSouth()}&maxx=${b.getEast()}&maxy=${b.getNorth()}`);
      map.getSource("zoning-fc")?.setData(data);
    } catch { /* ignore */ }
  }, [zoningShade]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (zoningShade) { refreshZoning(); map.on("moveend", refreshZoning); }
    else { map.off("moveend", refreshZoning); map.getSource("zoning-fc")?.setData({ type: "FeatureCollection", features: [] }); }
    return () => map.off("moveend", refreshZoning);
  }, [zoningShade, refreshZoning]);

  // Zoning category filter: apply a Mapbox layer filter (instant, no re-fetch).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer("zoning-fc-fill")) return;
    const cats = Array.from(zoneFilter);
    const filter = (cats.length === 0 || cats.length >= ZONE_LEGEND.length)
      ? null : ["match", ["get", "category"], cats, true, false];
    map.setFilter("zoning-fc-fill", filter);
    map.setFilter("zoning-fc-line", filter);
  }, [zoneFilter, zoningShade]);

  // Load per-state Political + Regulatory classification for the USA-map overlays.
  useEffect(() => {
    api.get("/pi/market-map").then(({ data }) => setMarketMap(data.states || [])).catch(() => {});
  }, []);

  // Color + filter the USA states by Political / Development-Regulatory environment.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !marketMap.length) return;
    const apply = () => {
      if (!map.getLayer("states-fill")) { map.once("idle", apply); return; }
      const active = polOverlay || regOverlay;
      if (!active) { map.setPaintProperty("states-fill", "fill-opacity", 0); return; }
      const colorBy = polOverlay ? "political" : "regulatory";
      const palette = polOverlay ? POL_COLORS : REG_COLORS;
      const colorPairs = [];
      const visible = [];
      marketMap.forEach((s) => {
        const nameLc = s.name.toLowerCase();
        colorPairs.push(nameLc, palette[s[colorBy]] || "#3a3a3a");
        const passPol = !polOverlay || polFilter.has(s.political);
        const passReg = !regOverlay || regFilter.has(s.regulatory);
        if (passPol && passReg) visible.push(nameLc);
      });
      map.setPaintProperty("states-fill", "fill-color",
        ["match", ["downcase", ["get", "name"]], ...colorPairs, "#3a3a3a"]);
      map.setPaintProperty("states-fill", "fill-opacity",
        visible.length ? ["match", ["downcase", ["get", "name"]], visible, 0.5, 0] : 0);
    };
    apply();
  }, [marketMap, polOverlay, regOverlay, polFilter, regFilter]);

  // Draw the selected parcel boundary (amber) and fit to it when Regrid returns geometry.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const geo = record?.parcel_geometry;
    const draw = () => {
      if (!map.isStyleLoaded()) { map.once("idle", draw); return; }
      const fc = geo ? { type: "Feature", geometry: geo, properties: {} }
                     : { type: "FeatureCollection", features: [] };
      const src = map.getSource("parcel");
      if (src) { src.setData(fc); }
      else {
        map.addSource("parcel", { type: "geojson", data: fc });
        map.addLayer({ id: "parcel-fill", type: "fill", source: "parcel",
          paint: { "fill-color": "#f5b301", "fill-opacity": 0.18 } });
        map.addLayer({ id: "parcel-line", type: "line", source: "parcel",
          paint: { "line-color": "#f5b301", "line-width": 2.5 } });
      }
      if (geo?.coordinates) {
        const coords = [];
        const walk = (a) => { if (typeof a[0] === "number") coords.push(a); else a.forEach(walk); };
        walk(geo.coordinates);
        if (coords.length) {
          const lons = coords.map((c) => c[0]), lats = coords.map((c) => c[1]);
          map.fitBounds([[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
            { padding: 140, maxZoom: 18, duration: 800 });
        }
      }
      // Optional zoning color-shade of the selected parcel.
      const color = zoningShade ? ZONE_COLORS[zoningCategory(record)] : "#f5b301";
      if (map.getLayer("parcel-fill")) {
        map.setPaintProperty("parcel-fill", "fill-color", color);
        map.setPaintProperty("parcel-fill", "fill-opacity", zoningShade && geo ? 0.4 : 0.18);
      }
      if (map.getLayer("parcel-line")) map.setPaintProperty("parcel-line", "line-color", color);
    };
    draw();
  }, [record, zoningShade]);

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    if (queryType === "apn") {
      setSearching(true);
      try {
        const { data } = await api.get(`/pi/parcel-by-apn?apn=${encodeURIComponent(query.trim())}`);
        if (data?.lat != null && data?.lng != null) {
          mapRef.current?.flyTo({ center: [data.lng, data.lat], zoom: 17 });
          markerRef.current?.remove();
          markerRef.current = new mapboxgl.Marker({ color: "#27BEAA" }).setLngLat([data.lng, data.lat]).addTo(mapRef.current);
          runLookup({ lat: data.lat, lng: data.lng, apn: data.apn, parcelGeometry: data.geometry, qt: "apn" });
        } else {
          toast.info("APN not found in wired county coverage.");
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          toast.info("That APN isn't in the public parcel data for this area. Double-check the number, or search by address instead.");
        } else {
          toast.error(apiError(err));
        }
      } finally {
        setSearching(false);
        setResults([]);
      }
      return;
    }
    setSearching(true);
    try {
      const url = new URL("https://api.mapbox.com/search/geocode/v6/forward");
      url.searchParams.set("q", query.trim());
      url.searchParams.set("country", "US");
      url.searchParams.set("limit", "5");
      url.searchParams.set("access_token", TOKEN);
      const res = await fetch(url);
      const j = await res.json();
      setResults(j.features || []);
      if (!j.features?.length) toast.info("No matches found.");
    } catch {
      toast.error("Search failed. Check the Mapbox token.");
    } finally {
      setSearching(false);
    }
  };

  const chooseResult = (feat) => {
    const [lng, lat] = feat.geometry.coordinates;
    mapRef.current?.flyTo({ center: [lng, lat], zoom: 16 });
    markerRef.current?.remove();
    markerRef.current = new mapboxgl.Marker({ color: "#27BEAA" }).setLngLat([lng, lat]).addTo(mapRef.current);
    const label = feat.properties?.full_address || feat.properties?.name || query;
    setResults([]);
    runLookup({ lat, lng, q: label, qt: "address" });
  };

  const openProviders = async () => {
    setShowProviders(true);
    if (!providers.length) {
      try {
        const { data } = await api.get("/pi/providers");
        setProviders(data.providers || []);
      } catch (e) { toast.error(apiError(e)); }
    }
  };

  // resource: "property_search" = Property Screening, "inbound_leads" = the Leads module.
  // ⚠️ Do NOT pass "leads" here to mean the Leads module — "leads" is the Property Analysis
  // collection (legacy name). Use "inbound_leads" for the Leads module.
  useEffect(() => { loadPins(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    showPinsRef.current = showPins;
    const m = mapRef.current;
    if (m?.getLayer("saved-pins-circle")) m.setLayoutProperty("saved-pins-circle", "visibility", showPins ? "visible" : "none");
  }, [showPins, pinCount]);

  const saveToArqitek = async (resource = "property_search", allowDuplicate = false) => {
    if (!record) return;
    const setBusy = resource === "inbound_leads" ? setSavingLeads : setSaving;
    setBusy(true);
    try {
      const lead = leadRef.current;
      const updateId = lead?.updateLeadId || null;
      // Only re-save into a record that THIS parcel already created; any other parcel makes a new one.
      const savedHere = record.arqitek?.saved && record.arqitek?.resource === resource ? record.arqitek.record_id : null;
      // For a brand-new Lead, carry the Market (On/Off) and Property Type the user picked.
      const newLeadChoices = (!updateId && resource === "inbound_leads")
        ? {
            ...(saveMarket === "Off-Market"
              ? { source_type: "OFF-MARKET", property_source: "Off-Market" }
              : { source_type: "ACTIVE", property_source: "On-Market" }),
            ...(savePropertyType ? { property_classification: savePropertyType } : {}),
          }
        : {};
      const { data } = await api.post(`/pi/records/${record.id}/save-to-arqitek`, {
        resource,
        record_id: updateId || savedHere || null,
        extra_fields: updateId ? null : { ...(lead?.leadFields || {}), ...newLeadChoices },
        source_lead_id: updateId ? null : (lead?.leadId || null),
        allow_duplicate: allowDuplicate,
        lead_update: !!updateId,
      });
      setRecord((r) => ({ ...r, arqitek: { saved: true, resource: data.resource, record_id: data.record_id } }));
      toast.success(updateId
        ? `Land Recon data saved back to the lead (${data.fields_written} fields).`
        : `Saved to ${resource === "inbound_leads" ? "Leads" : "Property Screening"} (${data.fields_written} fields).`);
      leadRef.current = null;
      setLeadBanner(null);
      setLeadUpdateMode(false);
      setDupInfo(null);
      setPostSave({ resource, recordId: data.record_id });
      // Show the new pin right away; the full pin list refreshes in the background.
      if (!updateId && record?.lat != null && record?.lng != null) {
        const lbl = resource === "inbound_leads" ? "Lead" : "Property Screening";
        pinsRef.current = { ...pinsRef.current, features: [...pinsRef.current.features, {
          type: "Feature", geometry: { type: "Point", coordinates: [Number(record.lng), Number(record.lat)] },
          properties: { id: data.record_id, resource, stage: lbl, label: lbl, name: record.categories?.identity?.address?.value || "Property",
            address: "", apn: "", status: "", archived: false } }] };
        setPinCount(pinsRef.current.features.length);
        mapRef.current?.getSource("saved-pins")?.setData(pinsRef.current);
      }
      loadPins();
    } catch (e) {
      const d = duplicateFromError(e);
      if (d) setDupInfo({ ...d, resource });
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const runSkipTrace = async () => {
    if (!record) return;
    setSkipTracing(true);
    try {
      const { data } = await api.post(`/pi/records/${record.id}/skip-trace`);
      if (data.ownership) setRecord((r) => (r && r.id === record.id) ? { ...r, categories: { ...r.categories, ownership: data.ownership } } : r);
      if (data.error) { toast.error(data.error); }
      else if (data.matched) { setOpenCats((o) => ({ ...o, ownership: true })); toast.success("Owner found via skip trace."); }
      else { toast.message("No owner match found for this address."); }
    } catch (e) { toast.error(apiError(e)); } finally { setSkipTracing(false); }
  };

  // Status badge reflects only the field data points shown in the panel.
  const sc = (() => {
    const counts = { Confirmed: 0, Preliminary: 0, "Needs Verification": 0, Unknown: 0 };
    const cats = record?.categories || {};
    for (const { key } of CATEGORY_META) {
      const cat = cats[key];
      if (!cat) continue;
      for (const f of Object.values(cat)) {
        const s = f?.verification_status || "Unknown";
        counts[s] = (counts[s] || 0) + 1;
      }
    }
    return counts;
  })();

  return (
    <div className="relative flex h-[calc(100vh_-_4rem)] overflow-hidden" data-testid="property-intelligence-page">
      {/* MAP */}
      <div className="relative flex-1 min-w-0 overflow-hidden">
        {!TOKEN && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/80 text-sm text-muted-foreground">
            Missing REACT_APP_MAPBOX_TOKEN — map cannot load.
          </div>
        )}
        <div ref={containerRef} className="h-full w-full" data-testid="pi-map" />

        {/* Search overlay */}
        <div className="absolute top-4 left-4 z-10 w-[min(92vw,460px)] max-w-[calc(100%-5rem)]">
          <form onSubmit={doSearch} className="flex gap-2 rounded-xl border border-border bg-card/95 backdrop-blur p-2 shadow-lg">
            <Select value={queryType} onValueChange={setQueryType}>
              <SelectTrigger className="w-[110px] h-9 text-xs" data-testid="pi-search-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="apn">APN</SelectItem>
                <SelectItem value="address">Address</SelectItem>
                <SelectItem value="city">City</SelectItem>
                <SelectItem value="zip">ZIP</SelectItem>
                <SelectItem value="county">County</SelectItem>
                <SelectItem value="state">State</SelectItem>
              </SelectContent>
            </Select>
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={queryType === "apn" ? "Enter APN / parcel #" : "Search address, city, county, ZIP…"}
              className="h-9 flex-1" data-testid="pi-search-input" />
            <Button type="submit" size="sm" className="h-9" disabled={searching} data-testid="pi-search-btn">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </form>
          {results.length > 0 && (
            <div className="mt-1.5 rounded-xl border border-border bg-card/95 backdrop-blur shadow-lg overflow-hidden" data-testid="pi-search-results">
              {results.map((f) => (
                <button key={f.id} onClick={() => chooseResult(f)}
                  className="block w-full text-left px-3 py-2.5 text-sm hover:bg-accent transition-colors border-b border-border/60 last:border-0">
                  {f.properties?.full_address || f.properties?.name}
                </button>
              ))}
            </div>
          )}
          {leadBanner && (
            <div className="mt-1.5 rounded-xl border border-primary/40 bg-primary/10 backdrop-blur px-3 py-2.5 shadow-lg flex items-start gap-2" data-testid="pi-lead-banner">
              <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <div className="text-xs leading-relaxed text-foreground">
                {leadUpdateMode ? (
                  <>Running Land Recon for <span className="font-semibold">{leadBanner}</span>. Review the intelligence below, then <span className="font-semibold">Save to Leads</span> to write the recon data back to this lead.</>
                ) : (
                  <>Converting lead <span className="font-semibold">{leadBanner}</span>. Review the intelligence below, then <span className="font-semibold">Save to Property Screening</span> to carry all lead data over.</>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Map controls */}
        <div className="absolute bottom-4 left-4 right-4 z-10 flex gap-2 overflow-x-auto no-scrollbar">
          <Button size="sm" variant="secondary" className="h-9 shadow" onClick={() => setSatellite((s) => !s)} data-testid="pi-satellite-toggle">
            <Layers className="h-4 w-4 mr-1.5" />{satellite ? "Map" : "Satellite"}
          </Button>
          <Button size="sm" variant={zoningShade ? "default" : "secondary"} className="h-9 shadow" onClick={() => setZoningShade((s) => !s)} data-testid="pi-zoning-toggle">
            <Landmark className="h-4 w-4 mr-1.5" />Zoning
          </Button>
          <Button size="sm" variant={polOverlay ? "default" : "secondary"} className="h-9 shadow" onClick={() => setPolOverlay((s) => !s)} data-testid="pi-political-toggle">
            <Vote className="h-4 w-4 mr-1.5" />Political
          </Button>
          <Button size="sm" variant={regOverlay ? "default" : "secondary"} className="h-9 shadow" onClick={() => setRegOverlay((s) => !s)} data-testid="pi-regulatory-toggle">
            <Scale className="h-4 w-4 mr-1.5" />Regulatory
          </Button>
          <Button size="sm" variant={showPins ? "default" : "secondary"} className="h-9 shadow" onClick={() => setShowPins((v) => !v)} data-testid="pi-saved-pins-toggle">
            <MapPin className="h-4 w-4 mr-1.5" />Saved ({pinCount})
          </Button>
          <Button size="sm" variant="secondary" className="h-9 shadow" onClick={openProviders} data-testid="pi-providers-btn">
            <Database className="h-4 w-4 mr-1.5" />Data Sources
          </Button>
          <Button size="sm" variant="secondary" className="h-9 shadow" onClick={() => setShowFilters(true)} data-testid="pi-filters-btn">
            <SlidersHorizontal className="h-4 w-4 mr-1.5" />Filters
          </Button>
        </div>

        {(zoningShade || polOverlay || regOverlay) && (
          <div className="absolute top-4 right-14 z-10 flex flex-col gap-2 items-end">
            {zoningShade && (
            <div className="rounded-lg border border-border bg-card/95 backdrop-blur px-3 py-2.5 shadow" data-testid="pi-zoning-legend">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] font-semibold text-foreground">Zoning</div>
              <button className="text-[10px] text-primary hover:underline" onClick={() => setZoneFilter(new Set(ZONE_LEGEND.map((z) => z[0])))} data-testid="pi-zoning-filter-all">All</button>
            </div>
            {ZONE_LEGEND.map(([key, label, c]) => {
              const active = zoneFilter.has(key);
              return (
                <button key={key} onClick={() => toggleCat(key)} data-testid={`pi-zoning-filter-${key}`}
                  className={`flex items-center gap-2 text-[11px] w-full py-0.5 transition-opacity ${active ? "opacity-100" : "opacity-40"}`}>
                  <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: c }} />
                  <span className={active ? "text-foreground" : "text-muted-foreground"}>{label}</span>
                </button>
              );
            })}
            <div className="text-[10px] text-muted-foreground/60 mt-1 max-w-[150px]">Zoom in to load parcels. Click a category to filter. Preliminary — verify with jurisdiction.</div>
            </div>
            )}

            {polOverlay && (
            <div className="rounded-lg border border-border bg-card/95 backdrop-blur px-3 py-2.5 shadow" data-testid="pi-political-legend">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] font-semibold text-foreground">Political Environment</div>
                <button className="text-[10px] text-primary hover:underline" onClick={() => setPolFilter(new Set(POL_LEGEND.map((p) => p[0])))} data-testid="pi-political-filter-all">All</button>
              </div>
              {POL_LEGEND.map(([key, label, c]) => {
                const active = polFilter.has(key);
                return (
                  <button key={key} onClick={() => togglePol(key)} data-testid={`pi-political-filter-${key}`}
                    className={`flex items-center gap-2 text-[11px] w-full py-0.5 transition-opacity ${active ? "opacity-100" : "opacity-40"}`}>
                    <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: c }} />
                    <span className={active ? "text-foreground" : "text-muted-foreground"}>{label}</span>
                  </button>
                );
              })}
              <div className="text-[10px] text-muted-foreground/60 mt-1 max-w-[160px]">Market context only — not a measure of development difficulty.</div>
            </div>
            )}

            {regOverlay && (
            <div className="rounded-lg border border-border bg-card/95 backdrop-blur px-3 py-2.5 shadow" data-testid="pi-regulatory-legend">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] font-semibold text-foreground">Development Regulation</div>
                <button className="text-[10px] text-primary hover:underline" onClick={() => setRegFilter(new Set(REG_LEGEND.map((r) => r[0])))} data-testid="pi-regulatory-filter-all">All</button>
              </div>
              {REG_LEGEND.map(([key, label, c]) => {
                const active = regFilter.has(key);
                return (
                  <button key={key} onClick={() => toggleReg(key)} data-testid={`pi-regulatory-filter-${key}`}
                    className={`flex items-center gap-2 text-[11px] w-full py-0.5 transition-opacity ${active ? "opacity-100" : "opacity-40"}`}>
                    <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: c }} />
                    <span className={active ? "text-foreground" : "text-muted-foreground"}>{label}</span>
                  </button>
                );
              })}
              <div className="text-[10px] text-muted-foreground/60 mt-1 max-w-[160px]">State baseline; select a property for the local (City/County) rating. Preliminary.</div>
            </div>
            )}
          </div>
        )}

        {!panelOpen && (record || loadingRecord) && (
          <button onClick={() => setPanelOpen(true)} title="Show property details" aria-label="Show property details"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 flex items-center gap-1.5 rounded-l-lg border border-r-0 border-border bg-card/95 backdrop-blur pl-2.5 pr-2 py-3 text-xs font-medium text-foreground shadow-lg hover:text-primary transition-colors"
            data-testid="pi-panel-open">
            <PanelRightOpen className="h-4 w-4 text-primary" />
            <span className="hidden sm:inline">Details</span>
          </button>
        )}

        {!panelOpen && (
          <div className="hidden sm:block absolute bottom-16 right-4 z-10 rounded-lg border border-border bg-card/95 backdrop-blur px-3 py-2 text-xs text-muted-foreground shadow max-w-[260px]" data-testid="pi-map-hint">
            <MapPin className="h-3.5 w-3.5 inline mr-1 text-primary" />
            {mapZoom < 13
              ? "Zoom in to street level to see individual parcel lots (amber outlines)."
              : parcelCoverage === true
                ? (mapZoom < 16.5
                    ? "Parcel lots shown. Zoom in a bit more to reveal APN labels — hover or click a lot."
                    : "Hover a lot to see its APN, or click it to build a Land Recon record.")
                : mapZoom < 15
                  ? "Zoom in a bit more to load parcel lots for this area."
                  : "No parcel lots here — this area isn't in current coverage. Wired counties: LA, San Diego, Riverside, San Bernardino (CA), Maricopa (AZ), Mecklenburg, Wake (NC), Aiken, Laurens, Greenville, Spartanburg (SC)."}
          </div>
        )}
      </div>

      {/* RECORD PANEL */}
      {panelOpen && (
        <div className="absolute inset-y-0 right-0 z-30 w-full lg:relative lg:inset-auto lg:w-[420px] lg:max-w-[90vw] lg:border-l shrink-0 border-border bg-card flex flex-col" data-testid="pi-panel">
          <div className="flex items-center justify-between px-4 h-14 border-b border-border shrink-0">
            <div className="flex items-center gap-2 font-semibold text-sm"><Sparkles className="h-4 w-4 text-primary" />Land Recon</div>
            <button onClick={() => setPanelOpen(false)} title="Collapse panel" aria-label="Collapse panel"
              className="p-1.5 -mr-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" data-testid="pi-panel-close">
              <PanelRightClose className="h-4 w-4" />
            </button>
          </div>

          {loadingRecord ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-sm">Gathering data from connected sources…</span>
            </div>
          ) : record ? (
            <div className="flex-1 overflow-y-auto slim-scroll">
              {/* Summary bar */}
              <div className="px-4 py-3 border-b border-border">
                <div className="text-sm font-medium text-foreground mb-2">
                  {record.categories?.identity?.address?.value || record.categories?.identity?.city?.value || "Selected location"}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-[11px] rounded-full px-2 py-0.5 bg-emerald-500/15 text-emerald-500">{sc.Confirmed || 0} Confirmed</span>
                  <span className="text-[11px] rounded-full px-2 py-0.5 bg-amber-500/15 text-amber-500">{sc.Preliminary || 0} Preliminary</span>
                  <span className="text-[11px] rounded-full px-2 py-0.5 bg-red-500/15 text-red-500">{sc["Needs Verification"] || 0} Needs Verify</span>
                  <span className="text-[11px] rounded-full px-2 py-0.5 bg-muted text-muted-foreground">{sc.Unknown || 0} Unknown</span>
                </div>
                {record.conflicts?.length > 0 && (
                  <div className="mt-2 text-[11px] text-red-500 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />{record.conflicts.length} conflicting data point(s) detected
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-4 py-3 border-b border-border">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="secondary" className="w-full" disabled={savingLeads} data-testid="pi-save-leads-btn">
                      {savingLeads ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                      {leadUpdateMode ? "Save to Lead" : (savedLeadHere ? "Saved to Leads" : "Save to Leads")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="pi-save-leads-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{leadUpdateMode ? "Save to this lead?" : "Save to Leads?"}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {leadUpdateMode
                          ? "Write this property's Land Recon data back to the existing lead. Only fields Land Recon resolved are updated; your other lead data is kept."
                          : (<>Create a new Lead from this property's intelligence data.{savedLeadHere ? " The Lead you already created for this parcel will be updated." : ""}</>)}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    {!leadUpdateMode && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-1">
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-muted-foreground">Market</label>
                          <Select value={saveMarket} onValueChange={setSaveMarket}>
                            <SelectTrigger className="h-9" data-testid="pi-save-market"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="On-Market">On-Market</SelectItem>
                              <SelectItem value="Off-Market">Off-Market</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-muted-foreground">Property Type</label>
                          <Select value={savePropertyType} onValueChange={setSavePropertyType}>
                            <SelectTrigger className="h-9" data-testid="pi-save-property-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Residential">Residential</SelectItem>
                              <SelectItem value="Commercial">Commercial</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="pi-save-leads-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => saveToArqitek("inbound_leads")} data-testid="pi-save-leads-confirm">
                        Save to Leads
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Categories */}
              {CATEGORY_META.map(({ key, label, icon: Icon }) => {
                const cat = record.categories?.[key];
                if (!cat) return null;
                const open = openCats[key];
                return (
                  <div key={key} className="border-b border-border">
                    <button onClick={() => setOpenCats((o) => ({ ...o, [key]: !o[key] }))}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors" data-testid={`pi-cat-${key}`}>
                      <span className="flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4 text-primary" />{label}
                        {key === "zoning" && zoningLoading && (
                          <span className="flex items-center gap-1 text-[10px] font-normal text-primary" data-testid="pi-zoning-loading">
                            <span className="h-2.5 w-2.5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                            Angela reading…
                          </span>
                        )}
                        {key === "ownership" && (
                          <span role="button" tabIndex={0}
                            title="Paid on-demand lookup (BatchData). Owner name & mailing address usually come free from county records — use this when they're missing or you want phone/email to mail the owner."
                            onClick={(e) => { e.stopPropagation(); if (!skipTracing) runSkipTrace(); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); if (!skipTracing) runSkipTrace(); } }}
                            className="inline-flex items-center gap-1 rounded-md border border-primary/40 px-2 py-0.5 text-[10px] font-normal text-primary hover:bg-primary/10 transition-colors"
                            data-testid="pi-skiptrace-btn">
                            {skipTracing
                              ? <><span className="h-2.5 w-2.5 rounded-full border-2 border-primary border-t-transparent animate-spin" />Looking up…</>
                              : <><Mail className="h-3 w-3" />Owner lookup (to mail)</>}
                          </span>
                        )}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <div className="px-4 pb-3">
                        {Object.entries(cat).map(([fk, field]) => <FieldRow key={fk} field={field} />)}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="px-4 py-3 text-[11px] text-muted-foreground/70">
                Sources: {record.sources_used?.join(", ") || "none"}. Public/third-party data is not final legal verification.
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Providers dialog */}
      <Dialog open={showProviders} onOpenChange={setShowProviders}>
        <DialogContent className="max-w-lg" data-testid="pi-providers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Database className="h-4 w-4 text-primary" />Data Sources</DialogTitle>
            <DialogDescription>Land Recon normalizes many replaceable sources into one record. Connected sources populate data now; others activate when you connect a provider.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto slim-scroll">
            {providers.map((p) => (
              <div key={p.name} className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <div className="text-sm font-medium">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground capitalize">{p.category} · {p.type}</div>
                </div>
                {p.connected
                  ? <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30"><ShieldCheck className="h-3 w-3 mr-1" />Connected</Badge>
                  : <Badge variant="outline" className="text-muted-foreground">Needs API key</Badge>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters dialog */}
      <Dialog open={showFilters} onOpenChange={setShowFilters}>
        <DialogContent className="max-w-lg" data-testid="pi-filters-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4 text-primary" />Discovery Filters</DialogTitle>
            <DialogDescription>Filter the map for development opportunities. Filters activate as their underlying parcel/zoning data sources are connected.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {DISCOVERY_FILTERS.map((f) => (
              <span key={f} className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground opacity-70">{f}</span>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Connect a parcel data provider (e.g. Regrid) to enable live opportunity filtering across the map.</p>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!postSave} onOpenChange={(o) => { if (!o) setPostSave(null); }}>
        <AlertDialogContent data-testid="pi-post-save-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Saved to {postSave?.resource === "inbound_leads" ? "Leads" : "Property Screening"}</AlertDialogTitle>
            <AlertDialogDescription>Open the record now, or stay on the Land Recon map and keep researching.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPostSave(null)} data-testid="pi-post-save-stay">Stay on Map</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const p = postSave; setPostSave(null); navigate(`/record/${p.resource}/${p.recordId}`); }}
              data-testid="pi-post-save-go">Go to {postSave?.resource === "inbound_leads" ? "Lead" : "Record"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DuplicateApnDialog dup={dupInfo} busy={saving || savingLeads} onClose={() => setDupInfo(null)}
        onCreateAnyway={() => saveToArqitek(dupInfo.resource, true)} />
    </div>
  );
}
